import { useEffect, useRef, type RefObject } from 'react';

export interface UseAudioRecortadoParams {
  audioRef:  RefObject<HTMLAudioElement | null>;
  ativo:     boolean;
  inicioSeg: number;
  fimSeg:    number;
  /**
   * Fonte carregada no `<audio>` (`src`). Quando muda com `ativo=true`, o
   * efeito reinicia seek/play na nova fonte — sem isso, consumidores que
   * mantêm `ativo` true ao trocar de URL (prévia admin, config Realtime no
   * Player) herdam desync tocando/src.
   */
  src?: string | null;
  /** Chamado uma vez quando a reprodução é pausada por atingir fimSeg. */
  onFimAlcancado?: () => void;
}

/** Tolerância (s) abaixo da qual o seek não muda a posição de forma perceptível e `seeked` pode não vir. */
const SEEK_SEM_EFEITO_SEG = 0.25;

/**
 * Rede de segurança caso `seeked` nunca chegue. Precisa ser bem maior que o seek mais lento esperado
 * (TV Box com buffer frio, rede lenta): com 500 ms o play() saía antes do seek terminar e a locução
 * tocava o trecho anterior ao recorte.
 */
const SEEK_FALLBACK_MS = 3000;

/** Quantas vezes o fallback adia por o seek ainda estar em andamento (`audio.seeking`) — teto de ~15 s. */
const SEEK_FALLBACK_ADIAMENTOS = 4;

/**
 * Toca um <audio> recortado em [inicioSeg, fimSeg] — usado tanto pelo Player
 * (useMomentoEspecial, TV) quanto pela prévia de configuração no admin
 * (RecorteAudioPreview). play() só dispara depois de confirmação real de
 * que o seek pro início configurado terminou (evento `seeked`), nunca por
 * um timer fixo adivinhando quanto tempo o carregamento leva — timer fixo
 * corria contra o carregamento real dos metadados/buffer em produção (rede
 * mais lenta, primeiro play depois da TV ligar, cache frio) e podia
 * disparar o play() antes do seek acontecer, tocando a música inteira
 * desde o início em vez do trecho configurado.
 */
export function useAudioRecortado({
  audioRef, ativo, inicioSeg, fimSeg, src, onFimAlcancado,
}: UseAudioRecortadoParams): void {
  const onFimAlcancadoRef = useRef(onFimAlcancado);
  useEffect(() => { onFimAlcancadoRef.current = onFimAlcancado; });

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!ativo) {
      audio.pause();
      audio.currentTime = 0;
      return;
    }

    // `audio` já foi narrado para não-nulo acima, mas closures de function
    // declaration não preservam a narrowing do TypeScript — as assertions
    // abaixo são seguras porque `audio` é uma referência const capturada,
    // nunca reatribuída dentro deste efeito.
    let seekTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let jaIniciou = false;

    function iniciarReproducao() {
      if (jaIniciou) return;
      jaIniciou = true;
      audio!.play().catch(() => {
        audio!.muted = true;
        audio!.play()
          .then(() => { audio!.muted = false; })
          .catch(() => {});
      });
    }

    function onSeeked() {
      audio!.removeEventListener('seeked', onSeeked);
      if (seekTimeoutId) { clearTimeout(seekTimeoutId); seekTimeoutId = null; }
      iniciarReproducao();
    }

    // Fallback: se o seek ainda está correndo (TV Box com buffer frio), tocar agora soaria o trecho
    // anterior ao recorte — adia até o teto e só então desiste.
    let adiamentos = 0;
    function onSeekTimeout() {
      if (audio!.seeking && adiamentos < SEEK_FALLBACK_ADIAMENTOS) {
        adiamentos += 1;
        seekTimeoutId = setTimeout(onSeekTimeout, SEEK_FALLBACK_MS);
        return;
      }
      onSeeked();
    }

    function aplicarRecorteEIniciar() {
      // Sem seek real (início 0 ou já na posição do recorte) o navegador não dispara `seeked`.
      if (inicioSeg <= 0 || Math.abs(audio!.currentTime - inicioSeg) < SEEK_SEM_EFEITO_SEG) {
        iniciarReproducao();
        return;
      }
      audio!.addEventListener('seeked', onSeeked, { once: true });
      audio!.currentTime = inicioSeg;
      // Rede de segurança pra nunca travar esperando um evento que pode não vir
      // (o seeked real cancela este timer em onSeeked).
      seekTimeoutId = setTimeout(onSeekTimeout, SEEK_FALLBACK_MS);
    }

    if (audio.readyState >= 2) {
      aplicarRecorteEIniciar();
    } else {
      audio.addEventListener('loadeddata', aplicarRecorteEIniciar, { once: true });
    }

    function onTimeUpdate() {
      if (audio!.currentTime >= fimSeg) {
        audio!.pause();
        onFimAlcancadoRef.current?.();
      }
    }
    audio.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      if (seekTimeoutId) clearTimeout(seekTimeoutId);
      audio.removeEventListener('loadeddata', aplicarRecorteEIniciar);
      audio.removeEventListener('seeked', onSeeked);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      // Pausa ao trocar src/recorte com ativo ainda true — evita sobrepor
      // a reprodução antiga enquanto o efeito reinicia na nova fonte.
      audio.pause();
    };
  // `src` identifica qual arquivo está no elemento; omitir reintroduz
  // desync quando ativo permanece true ao trocar a URL do <audio>.
  }, [audioRef, ativo, inicioSeg, fimSeg, src]);
}
